import { motion, AnimatePresence } from "framer-motion";
import { Globe, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { THREAD_VISIBILITY, type ThreadVisibility } from "@forgepoint/shared";

/** Props for the VisibilityToggle component. */
interface VisibilityToggleProps {
  visibility: ThreadVisibility;
  onToggle: () => void;
  disabled?: boolean;
}

/**
 * Animated toggle for setting thread visibility before sending.
 * Crossfades between Globe/Lock icons with a smooth background
 * color morph. Respects reduced-motion preferences.
 * @param props - The component props
 * @returns The visibility toggle button with tooltip
 */
export function VisibilityToggle({
  visibility,
  onToggle,
  disabled,
}: VisibilityToggleProps) {
  const isPrivate = visibility === THREAD_VISIBILITY.PRIVATE;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggle}
          disabled={disabled}
          aria-label={
            isPrivate
              ? "Visibility: Private. Click to switch to Organization."
              : "Visibility: Organization. Click to switch to Private."
          }
          className={cn(
            "gap-1.5 h-7 px-2.5 rounded-full text-xs font-medium transition-colors duration-300 motion-reduce:transition-none",
            isPrivate
              ? "bg-warning/10 text-warning-foreground hover:bg-warning/15"
              : "bg-info/10 text-info-foreground hover:bg-info/15",
          )}
        >
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              key={isPrivate ? "lock" : "globe"}
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.6, opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="flex items-center"
            >
              {isPrivate ? (
                <Lock className="h-3 w-3" />
              ) : (
                <Globe className="h-3 w-3" />
              )}
            </motion.span>
          </AnimatePresence>
          <span>{isPrivate ? "Private" : "Organization"}</span>
        </Button>
      </TooltipTrigger>
      <TooltipContent side="top">
        {isPrivate
          ? "Only you can see this conversation"
          : "Visible to everyone in your organization"}
      </TooltipContent>
    </Tooltip>
  );
}
